import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Image,
    Stack,
    Divider,
    StylesProvider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useBrand} from '../../../contexts/brand-context'
import {useMultiStyleConfig, useBreakpointValue} from '@chakra-ui/react'

export const OrderDetailsSummary = ({
    orderSubtotal = 0,
    merchandizeTotalTax = 0,
    totalTax = 0,
    totalOrderDiscount = 0,
    shippingFee = 0,
    orderTotal = 0,
    currency,
    giftCardTotal = 0
}) => {
    // Ensure all numeric values are valid numbers to prevent NaN
    const safeOrderSubtotal = Number(orderSubtotal) || 0
    const safeMerchandizeTotalTax = Number(merchandizeTotalTax) || 0
    const safeTotalTax = Number(totalTax) || 0
    const safeTotalOrderDiscount = Number(totalOrderDiscount) || 0
    const safeShippingFee = Number(shippingFee) || 0
    const safeOrderTotal = Number(orderTotal) || 0
    const safeGiftCardTotal = Number(giftCardTotal) || 0
    const {isGarage} = useBrand()
    const styles = useMultiStyleConfig('OrderDetail')

    return (
        <Stack spacing={0} lineHeight="18px">
            <Flex w="full" pb="20px">
                <Text {...styles.orderSummmaryHeadingText}>
                    <FormattedMessage
                        id="order_summary.heading.order_summary"
                        defaultMessage="Order Summary"
                    />
                </Text>
            </Flex>

            {/* TOTAL WITH TAXES */}
            <Flex w="full" justifyContent="space-between" pb="8px">
                <Text {...styles.orderSummarySubtotalText}>
                    <FormattedMessage
                        id="account_order_detail.label.order_subtotal_without_tax"
                        defaultMessage="Subtotal"
                    />
                </Text>
                <Text {...styles.orderSummaryCurrencyText}>
                    <FormattedNumber
                        style="currency"
                        currency={currency}
                        value={safeOrderSubtotal}
                    />
                </Text>
            </Flex>

            {safeTotalOrderDiscount > 0 && (
                <Flex w="full" justifyContent="space-between" pb="8px">
                    <Text {...styles.orderSummarySubtotalText}>
                        <FormattedMessage
                            id="account_order_detail.label.order_savings"
                            defaultMessage="Savings"
                        />
                    </Text>
                    <Text {...styles.orderSummaryCurrencyText}>
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={-safeTotalOrderDiscount}
                        />
                    </Text>
                </Flex>
            )}

            {safeGiftCardTotal > 0 && (
                <Flex w="full" justifyContent="space-between" pb="8px">
                    <Text {...styles.orderSummaryShippingText}>
                        <FormattedMessage
                            defaultMessage="Gift Card"
                            id="order_summary.label.gift_card"
                        />
                    </Text>
                    <Text {...styles.orderSummaryShippingText}>
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={-safeGiftCardTotal}
                        />
                    </Text>
                </Flex>
            )}

            {/* SHIPPING */}
            <Flex w="full" justifyContent="space-between" pb="8px">
                <Text {...styles.orderSummaryShippingText}>
                    <FormattedMessage
                        id="account_order_detail.label.order_shipping_fee"
                        defaultMessage="Shipping"
                    />
                </Text>
                <Text {...styles.orderSummaryShippingText}>
                    {safeShippingFee > 0 ? (
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={safeShippingFee}
                        />
                    ) : (
                        <FormattedMessage
                            id="checkout_confirmation.label.free"
                            defaultMessage="Free"
                        />
                    )}
                </Text>
            </Flex>

            {/* Taxation */}
            <Flex w="full" justifyContent="space-between" pb="8px">
                <Text {...styles.orderSummaryTaxesText}>
                    <FormattedMessage
                        id="account_order_detail.label.order_taxes"
                        defaultMessage="Taxes"
                    />
                </Text>
                <Text {...styles.orderSummaryTaxesText}>
                    <FormattedNumber
                        style="currency"
                        currency={currency}
                        value={safeTotalTax > 0 ? safeTotalTax : 0}
                    />
                </Text>
            </Flex>

            {/* GRAND TOTAL */}
            <Flex w="full" justifyContent="space-between" pt="20px">
                <Text {...styles.orderSummaryTotalHeading}>
                    {isGarage ? (
                        <FormattedMessage
                            id="account_order_detail.label.order_total.garage"
                            defaultMessage="Total"
                        />
                    ) : (
                        <FormattedMessage
                            id="account_order_detail.label.order_grand_total.dynamite"
                            defaultMessage="Total"
                        />
                    )}
                </Text>
                <Text {...styles.orderSummaryTotalValueText}>
                    <FormattedNumber
                        style="currency"
                        currency={currency}
                        value={safeOrderTotal - safeGiftCardTotal}
                    />
                </Text>
            </Flex>
        </Stack>
    )
}

OrderDetailsSummary.propTypes = {
    orderSubtotal: PropTypes.number,
    merchandizeTotalTax: PropTypes.number,
    totalTax: PropTypes.number,
    totalOrderDiscount: PropTypes.number,
    shippingFee: PropTypes.number,
    orderTotal: PropTypes.number,
    currency: PropTypes.string.isRequired,
    giftCardTotal: PropTypes.number
}
